/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useState, useRef, useEffect } from "react";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconComponent from "@CubeComponents/icons";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { Popper } from "@mui/material";
import type { PopperProps } from "@mui/material";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import get from "lodash/get";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import AddIcon from "@mui/icons-material/Add";

/* --------------------------------------------
   Types / Interfaces
   -------------------------------------------- */
/**
 * The object arms of a condition value. Date conditions store `{date}` or `{startDate,endDate}` and
 * FileSize stores `{size,unit}`; they are declared as one open shape rather than a union so the
 * type-specific branches below read them with a single erased view each.
 */
type FilterValueObject = {
  date?: Dayjs | string | null;
  startDate?: Dayjs | string | null;
  endDate?: Dayjs | string | null;
  size?: string | number | null;
  unit?: string;
};

/** Everything a filter condition's value can hold across the five filter types. */
export type FilterConditionValue = string | number | string[] | FilterValueObject | null | undefined;

// A `type` (not an `interface`) so it keeps the implicit index signature the dynamic
// `condition[key] = ...` writes below need.
export type FilterCondition = {
  field: string;
  operator: string;
  value: FilterConditionValue;
  logic?: string;
  name?: string;
  id?: string;
};

/** A leaf (or nested) predicate of the where-clause tree this modal builds. */
type FilterPredicate = {
  field?: string;
  operator?: string;
  value?: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
  condition?: string;
  predicates?: FilterPredicate[];
};

/**
 * One entry of the field -> condition map. Every field is optional because SlickGrid1's own
 * `FilterCondition` (which is fed to {@link getFilterPredicates} too) declares no `predicate`.
 */
type FilterConditionEntry = {
  id?: string;
  type?: string;
  option?: string;
  text?: unknown;
  predicate?: FilterPredicate[];
  logic?: string;
};

/** The remote query this modal clones to fetch suggestion options. */
export type FilterQuery = {
  where?: FilterPredicate[];
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
};

export interface ApiResponse {
  result: unknown[];
  data?: { result: unknown[] };
}

// A `type` so it satisfies AutoComplete's `Record<string, unknown>` option constraint.
export type ColumnDefinition = {
  field: string;
  filterType: string;
  _valueGetter?: (data: unknown) => unknown;
  /** Preferred over _valueGetter when building filter suggestion options (see SlickGrid1 ColumnDefinition). */
  _filterValueGetter?: (data: unknown) => unknown;
  id?: string;
  /** Column header display name - must be wrapped in t() for i18n */
  name?: TranslatedString;
  disableFiltering?: boolean;
};

export interface FilterModalProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  callRemote: (source: string, body: { value: FilterQuery }) => Promise<ApiResponse>;
  columnDefinitions: ColumnDefinition[];
  conditions: FilterCondition[];
  setConditions: React.Dispatch<React.SetStateAction<FilterCondition[]>>;
  width?: number;
  remoteDataSource: string | null;
  /** Optional: SlickGrid1's ToolBar only forwards one when the grid it belongs to was given one. */
  beforeDataBound?: (data: unknown) => unknown[];
  /** Optional for the same reason as `beforeDataBound`. */
  datasetForFetchingOptions?: unknown[];
  /** `null` whenever the modal runs against local data — the remote fetch is skipped then. */
  query: FilterQuery | null;
  localData: boolean;
  /**
   * Method shorthand (not an arrow property) on purpose: callers hand this their own
   * concretely-typed condition map / row type, which a contravariant parameter would reject.
   */
  getUpdatedFilterConditions(filterConditionsObj: Record<string, unknown>): unknown;
  /** Method shorthand for the same reason as {@link getUpdatedFilterConditions}. */
  applyFilterFunction(data: unknown[], field: string, operator: string, text: string, filterType: string): unknown[];
  gridRef: React.RefObject<unknown>;
  setAppliedFilters: React.Dispatch<React.SetStateAction<string[]>>;
  appliedFilters: string[];
  setFilterPredicates: React.Dispatch<React.SetStateAction<FilterPredicate | "">>;
  /** Optional for the same reason as `beforeDataBound`. */
  setFilterConditionsObject?: React.Dispatch<React.SetStateAction<Record<string, FilterConditionEntry>>>;
  tempCondition: FilterCondition | null;
  setTempCondition: React.Dispatch<React.SetStateAction<FilterCondition | null>>;
  currentColumnId?: string;
}

type TimeoutId = ReturnType<typeof setTimeout>;

/* --------------------------------------------
   Constants
   -------------------------------------------- */
/** Also the fallback operator list for a column whose `filterType` is none of the five below. */
const TEXT_FILTER_OPTIONS = [
  "Starts With",
  "Ends With",
  "Empty",
  "Equal",
  "Contains",
  "Does Not Start With",
  "Does Not End With",
  "Does Not Contain",
  "Not Equal",
  "Not Empty",
];

// Keyed by `ColumnDefinition.filterType`, which is a plain `string`: an unrecognised type has to
// read back as `undefined` (and fall back below) rather than be asserted into one of the five keys.
const FILTER_OPTIONS: Record<string, string[]> = {
  Text: TEXT_FILTER_OPTIONS,
  Number: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  Date: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Between",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  FileSize: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  MultiSelect: ["Equal"],
};

const TEXT_FIELD_DISABLED_OPTIONS = new Set<string>(["empty", "null", "notnull", "notempty"]);

/** Custom Popper so AutoComplete dropdowns render above the FilterModal (zIndex 1500) */
const ModalAutoCompletePopper = (props: PopperProps) => (
  <Popper
    {...props}
    sx={{ "& .MuiPaper-root": { overflowX: "hidden" }, "& .MuiAutocomplete-listbox": { overflowX: "hidden" } }}
    style={{ ...props.style, zIndex: 1501 }}
  />
);

/**
 * Popper for the column (field) picker — allows the dropdown to grow wider than the
 * input itself so long column names aren't truncated by the input's maxWidth.
 */
const FieldPickerPopper = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: 1501, width: "auto", minWidth: props.style?.width ?? undefined }}
    placement="bottom-start"
  />
);

/** Custom Popper so DatePicker calendars render above the FilterModal (zIndex 1500) */
const ModalDatePickerPopper = (props: PopperProps) => <Popper {...props} style={{ ...props.style, zIndex: 1501 }} />;

// Keyed by `filterType` for the same reason as FILTER_OPTIONS above.
const DEFAULT_FILTER_OPTIONS: Record<string, { option: string; text: FilterConditionValue }> = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: null } },
  Number: { option: "equal", text: "" },
  FileSize: { option: "greaterthan", text: { size: "", unit: "MB" } },
  MultiSelect: { option: "equal", text: [] },
};

const DEBOUNCE_MS = 800;
const NO_RECORDS_LABEL = "No Records Found";

/* --------------------------------------------
   Helper utilities (single responsibility)
   -------------------------------------------- */
const normalize = (str?: string) => (str ? str.toLowerCase().replaceAll(/\s+/g, "") : "");

/**
 * Erased view of a condition value's object arm. Date conditions store `{date}` /
 * `{startDate,endDate}` and FileSize stores `{size,unit}`; every other arm (string, number,
 * string[], null, undefined) carries none of those keys, so reading them off an empty object
 * yields exactly what reading them off the raw arm used to.
 */
const toValueObject = (value: FilterConditionValue): FilterValueObject =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};

/**
 * The `field` / `operator` / `logic` keys of a condition only ever carry a plain string, so a
 * non-string arriving on the shared change handlers reads as "nothing selected" instead of being
 * asserted into a string that `removeLastIndex` / `normalize` would then call `.replace` on.
 */
const toSelectionString = (value: FilterConditionValue): string => (typeof value === "string" ? value : "");

function removeLastIndex(field: string): string {
  if (!field) return field;
  return field.replace(/_\d+$/, "");
}

function getNullEmptyPredicate(field: string, operatorKey: string) {
  const isEmpty = operatorKey === "null" || operatorKey === "empty";
  const isNotEmpty = operatorKey === "notnull" || operatorKey === "notempty";

  if (isEmpty) {
    return [{ field, operator: "isempty" }];
  }
  if (isNotEmpty) {
    return [{ field, operator: "isnotempty" }];
  }
  return [];
}

function getDatePredicates(condition: FilterCondition, operatorKey: string) {
  // Reached only for Date columns, whose value is always the `{date}` / `{startDate,endDate}` arm.
  const { field } = condition;
  const value = toValueObject(condition.value);
  const hasSingle = value.date && dayjs(value.date).isValid();
  const hasStart = value.startDate && dayjs(value.startDate).isValid();
  const hasEnd = value.endDate && dayjs(value.endDate).isValid();

  if (operatorKey === "between") {
    if (!hasStart || !hasEnd) return [];
    return [
      { field, operator: "greaterthanorequal", value: dayjs(value.startDate).startOf("day").format() },
      { field, operator: "lessthanorequal", value: dayjs(value.endDate).endOf("day").format() },
    ];
  }

  if (operatorKey === "equal") {
    if (!hasSingle) return [];
    const d = dayjs(value.date);
    return [
      { field, operator: "greaterthanorequal", value: d.startOf("day").format() },
      { field, operator: "lessthanorequal", value: d.endOf("day").format() },
    ];
  }

  if (!hasSingle) return [];
  return [{ field, operator: operatorKey, value: dayjs(value.date).format() }];
}

function convertFileSizeToBytes(value: FilterConditionValue): number {
  if (typeof value === "number") return value;
  // The array arm carries no `size`, so it converted to 0 before this guard covered it too.
  if (!value || typeof value !== "object" || Array.isArray(value)) return 0;

  const size = Number(value.size) || 0;
  const unit = (value.unit || "B").toUpperCase();

  const multipliers: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };

  return size * (multipliers[unit] || 1);
}

function getDefaultPredicate(condition: FilterCondition, filterType: string, operatorKey: string) {
  const { field, value } = condition;

  if (filterType === "Number" && (value === "" || value === null || value === undefined)) {
    return [];
  }

  let filterValue: FilterConditionValue;

  if (value === undefined) {
    filterValue = filterType === "FileSize" ? 0 : "";
  } else if (filterType === "Number") {
    filterValue = Number(value);
  } else if (filterType === "FileSize") {
    filterValue = convertFileSizeToBytes(value);
  } else {
    filterValue = value;
  }

  if (filterType !== "Date" && (filterValue === "" || filterValue === null)) {
    return [];
  }

  return [{ field, operator: operatorKey, value: filterValue }];
}

/* Accepts a single condition and returns an array of predicates (or empty array if invalid) */
const getPredicateArray = (condition: FilterCondition, getFilterTypeForField: (field: string) => string) => {
  if (!condition.field) return [];

  const operatorKey = normalize(condition.operator);
  const filterType = getFilterTypeForField(condition.field);

  if (TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey)) {
    return getNullEmptyPredicate(condition.field, operatorKey);
  }

  if (filterType === "Date") {
    return getDatePredicates(condition, operatorKey);
  }

  if (filterType === "MultiSelect") {
    const selectedValues = Array.isArray(condition.value) ? condition.value : [];
    if (selectedValues.length === 0) return [];
    return selectedValues.map((value: string) => ({
      field: condition.field,
      operator: "equal",
      value,
    }));
  }

  return getDefaultPredicate(condition, filterType, operatorKey);
};

/* Build final SlickGrid-like predicate object from the map of field->filterCondition entries */
export const getFilterPredicates = (filterConditionsObj: Record<string, unknown>): FilterPredicate | "" => {
  const filterKeys = Object.keys(filterConditionsObj);
  if (filterKeys.length === 0) {
    return "";
  }

  const conditionsWithLogic: { logic: string; predicate: FilterPredicate }[] = [];

  for (const filterKey of filterKeys) {
    // The map is declared `Record<string, unknown>` so SlickGrid1's own filter-condition record
    // (which has no `predicate`) can be passed straight in, so every field is read back through a
    // runtime check instead of the entry being asserted into shape.
    const filterCondition = filterConditionsObj[filterKey];
    if (typeof filterCondition !== "object" || filterCondition === null) continue;

    const rawPredicates = "predicate" in filterCondition ? filterCondition.predicate : undefined;
    const predicates = Array.isArray(rawPredicates)
      ? rawPredicates.filter((p: unknown): p is FilterPredicate => typeof p === "object" && p !== null)
      : [];

    if (predicates.length === 0) continue;

    const logic = "logic" in filterCondition && typeof filterCondition.logic === "string" ? filterCondition.logic : "";
    const isMultiSelect = "type" in filterCondition && filterCondition.type === "MultiSelect";

    const enhancedPredicates = predicates.map((p: FilterPredicate) => ({
      ...p,
      isComplex: false,
      ignoreCase: true,
      ignoreAccent: false,
    }));

    if (enhancedPredicates.length === 1) {
      conditionsWithLogic.push({
        logic,
        // Length-checked on the line above; `noUncheckedIndexedAccess` does not track that.
        predicate: enhancedPredicates[0]!,
      });
    } else {
      conditionsWithLogic.push({
        logic,
        predicate: {
          isComplex: true,
          ignoreCase: true,
          ignoreAccent: false,
          condition: isMultiSelect ? "or" : "and",
          predicates: enhancedPredicates,
        },
      });
    }
  }

  if (conditionsWithLogic.length === 0) {
    return "";
  }

  // Length-checked above; `noUncheckedIndexedAccess` does not track that.
  if (conditionsWithLogic.length === 1) {
    return conditionsWithLogic[0]!.predicate;
  }

  let result = conditionsWithLogic[0]!.predicate;

  for (let i = 1; i < conditionsWithLogic.length; i++) {
    const currentCondition = conditionsWithLogic[i]!;
    const logic = currentCondition.logic || "and";

    result = {
      isComplex: true,
      ignoreCase: true,
      ignoreAccent: false,
      condition: logic,
      predicates: [result, currentCondition.predicate],
    };
  }

  return result;
};

const toDayjsOrNull = (v: dayjs.ConfigType) => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

/* --------------------------------------------
   Main FilterModal component
   -------------------------------------------- */
const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  anchorEl,
  onClose,
  columnDefinitions,
  conditions,
  setConditions,
  width = "40.625rem",
  remoteDataSource,
  beforeDataBound,
  datasetForFetchingOptions,
  query,
  localData,
  applyFilterFunction,
  gridRef,
  setAppliedFilters,
  setFilterPredicates,
  setFilterConditionsObject,
  tempCondition,
  setTempCondition,
  callRemote,
}) => {
  const { t } = useTranslation();

  /* UI / control state */
  const [optionsMap, setOptionsMap] = useState<Record<string, string[]>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const bottomRef = useRef<HTMLDivElement | null>(null);

  /* debounce timers per condition key */
  const debounceTimers = useRef<Record<string, TimeoutId>>({});

  /* -------------------------
     Utility: get column filter type
     ------------------------- */
  const getFilterTypeForField = (rawFieldName: string) => {
    const cleaned = removeLastIndex(rawFieldName);
    const column = columnDefinitions.find((col: ColumnDefinition) => col.field === cleaned);
    return column?.filterType || "Text";
  };

  /* -------------------------
     Small validators
     ------------------------- */
  const isValidDateValue = (d: dayjs.ConfigType) => !!d && dayjs(d).isValid();

  const isConditionReady = (cond: FilterCondition) => {
    if (!cond.field || !cond.operator) return false;
    const type = getFilterTypeForField(cond.field);
    const opKey = normalize(cond.operator);

    if (TEXT_FIELD_DISABLED_OPTIONS.has(opKey)) return true;

    // Each branch below reads only the value arm its filter type stores.
    if (type === "Date") {
      const value = toValueObject(cond.value);
      if (opKey === "between") {
        return isValidDateValue(value.startDate) && isValidDateValue(value.endDate);
      }
      return isValidDateValue(value.date);
    }

    if (type === "Number") {
      return cond.value !== "" && cond.value !== null && !Number.isNaN(Number(cond.value));
    }

    if (type === "FileSize") {
      const size = toValueObject(cond.value).size;
      return size !== "" && size !== null && size !== undefined && !Number.isNaN(Number(size));
    }

    if (type === "MultiSelect") {
      return Array.isArray(cond.value) && cond.value.length > 0;
    }

    return (cond.value ?? "").toString().trim().length > 0;
  };

  const isAllConditionsValid = () => conditions.every(isConditionReady);

  /* -------------------------
     Clean corrupted field names once on mount
     (if any condition contains trailing _\d+, remove it)
     ------------------------- */
  useEffect(() => {
    if (conditions.length === 0) return;
    const needsCleaning = conditions.some((c: FilterCondition) => c.field && /_\d+$/.test(c.field));
    if (needsCleaning) {
      const cleanedConditions = conditions.map((c: FilterCondition) => ({ ...c, field: removeLastIndex(c.field) }));
      setConditions(cleanedConditions);
    }
  }, []); // run once on mount

  /* Scroll to bottom when conditions or temp change (UX) */
  useEffect(() => {
    if ((conditions.length > 0 || tempCondition) && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conditions.length, tempCondition]);

  /* Cleanup debounce timers on unmount */
  useEffect(() => {
    const node = debounceTimers.current;
    return () => {
      Object.values(node).forEach((timer: TimeoutId) => clearTimeout(timer));
    };
  }, []);

  /* -------------------------
     Core: Build and apply filters to the grid
     ------------------------- */
  const applyFiltersToGrid = (onlyTheseConditions?: FilterCondition[]) => {
    const list = onlyTheseConditions ?? conditions;
    if (!gridRef.current) return;

    const filterConditionsObj: Record<string, FilterConditionEntry> = {};
    const readyFields: string[] = [];

    for (const [index, cond] of list.entries()) {
      if (cond.field && cond.operator) {
        const cleanedField = removeLastIndex(cond.field);

        const predicateArray = getPredicateArray({ ...cond, field: cleanedField }, getFilterTypeForField);
        if (predicateArray.length === 0) {
          continue;
        }

        const uniqueKey = `${cleanedField}_${index}`;

        filterConditionsObj[uniqueKey] = {
          id: cleanedField,
          type: getFilterTypeForField(cleanedField),
          option: normalize(cond.operator),
          text: cond.value,
          predicate: predicateArray,
          logic: cond.logic || "and",
        };

        if (!readyFields.includes(cleanedField)) {
          readyFields.push(cleanedField);
        }
      }
    }

    setAppliedFilters(readyFields);

    if (setFilterConditionsObject) {
      setFilterConditionsObject(filterConditionsObj);
    }

    // Remote data source: build where clause (predicate)
    if (remoteDataSource && Object.keys(filterConditionsObj).length > 0) {
      const whereClause = getFilterPredicates(filterConditionsObj);
      setFilterPredicates(whereClause);
    } else if (Object.keys(filterConditionsObj).length === 0) {
      setFilterPredicates("");
    }
  };

  const applyFiltersDebounced = (conditionKey: string) => {
    if (debounceTimers.current[conditionKey]) {
      clearTimeout(debounceTimers.current[conditionKey]!);
    }

    debounceTimers.current[conditionKey] = setTimeout(() => {
      const readyConds = conditions.filter(isConditionReady);
      applyFiltersToGrid(readyConds);
      delete debounceTimers.current[conditionKey];
    }, DEBOUNCE_MS);
  };

  /* -------------------------
     Fetch options for autocomplete (remote or local)
     ------------------------- */
  const fetchOptions = async (text: string, rawField: string, operator: string, conditionKey: string) => {
    const operatorKey = normalize(operator);
    // Allow empty text for these operators to show auto-suggestions
    const allowEmptyTextOperators = [
      "equal",
      "equals",
      "startswith",
      "contains",
      "endswith",
      "notequal",
      "notequals",
      "doesnotstartwith",
      "doesnotendwith",
      "doesnotcontain",
    ];
    if (!text && !allowEmptyTextOperators.includes(operatorKey)) return;

    // indicate loading
    setLoadingMap((prev) => ({ ...prev, [conditionKey]: true }));
    try {
      const cleanField = removeLastIndex(rawField);

      let filteredData: unknown[] = [];

      const filterType = getFilterTypeForField(cleanField);

      // For "equal" on text fields, use "startswith" when fetching suggestions
      // so partial typing returns matches (e.g. typing "Jo" shows "John", "Joseph")
      const suggestionOperator = operatorKey === "equal" && filterType === "Text" ? "startswith" : operatorKey;
      // For exact-match operators on non-Text fields (Number, Date, FileSize), partial
      // typed text won't match any record exactly — so we skip the predicate and let the
      // suggestion dropdown show all distinct values. Without this, autocomplete returns
      // 0 results during partial typing and the user sees a "No Records" illustration.
      const isExactMatchOnNonText =
        (operatorKey === "equal" || operatorKey === "notequal") &&
        filterType !== "Text" &&
        filterType !== "MultiSelect";

      if (remoteDataSource && query) {
        // For MultiSelect, fetch more records to build a comprehensive options list
        const modifiedQuery: FilterQuery = {
          ...query,
          skip: 0,
          take: filterType === "MultiSelect" ? 100 : 20,
          requiresCounts: true,
        };

        // Only add filter predicate if there's search text and not MultiSelect
        // MultiSelect fetches all data and filters options client-side
        if (text && filterType !== "MultiSelect" && !isExactMatchOnNonText) {
          const predicate = {
            field: cleanField,
            operator: suggestionOperator,
            value: text,
            isComplex: false,
            ignoreCase: true,
            ignoreAccent: false,
          };
          const where = query.where?.length ? [...query.where, predicate] : [predicate];
          modifiedQuery.where = where;
        }

        const rawResponse: ApiResponse = await callRemote(remoteDataSource, { value: modifiedQuery });
        let dataResult: unknown[] | undefined;
        if (beforeDataBound) {
          dataResult = beforeDataBound(rawResponse);
        } else if (Array.isArray(rawResponse.result)) {
          dataResult = rawResponse.result;
        } else {
          dataResult = rawResponse.data?.result;
        }
        filteredData = dataResult ?? [];
      } else if (localData && datasetForFetchingOptions) {
        // For auto-suggestions with empty text, use all data; otherwise apply filter.
        // Exact-match on non-Text columns (Number/Date/FileSize) cannot meaningfully
        // narrow during partial typing — show all distinct values instead.
        if (text && !isExactMatchOnNonText) {
          filteredData = applyFilterFunction(
            datasetForFetchingOptions,
            cleanField,
            suggestionOperator,
            text,
            filterType,
          );
        } else {
          // Return all data for auto-suggestions (limited to first 20 unique values)
          filteredData = datasetForFetchingOptions.slice(0, 100);
        }
      }

      const column = columnDefinitions.find((col: ColumnDefinition) => col.field === cleanField);
      // _filterValueGetter wins so suggestion options show the displayed value (e.g. a userTag
      // user name) instead of the token _valueGetter returns for rendering (e.g. the user id).
      const valueGetter = column?._filterValueGetter ?? column?._valueGetter;

      let rawOptions: string[];
      if (filterType === "MultiSelect") {
        // Flatten arrays returned by _valueGetter for MultiSelect columns
        const allValues: string[] = [];
        for (const d of filteredData) {
          const fieldValue = valueGetter ? valueGetter(d) : get(d, cleanField);
          if (Array.isArray(fieldValue)) {
            for (const item of fieldValue) {
              allValues.push(typeof item === "object" && item !== null && "name" in item ? item.name : String(item));
            }
          } else if (fieldValue) {
            allValues.push(
              typeof fieldValue === "object" && fieldValue !== null && "name" in fieldValue
                ? fieldValue.name
                : String(fieldValue),
            );
          }
        }
        rawOptions = [...new Set(allValues.filter(Boolean))].sort((a, b) => a.localeCompare(b));
      } else {
        // A column's `_valueGetter` / `_filterValueGetter` is declared as returning `unknown`, and
        // some return non-strings (FilesGridHelper returns `params.size || 0`). `optionsMap` holds
        // strings, so the option is coerced the same way the MultiSelect branch above coerces its
        // items, rather than asserted into one. A nullish read becomes "" and is dropped by the
        // `filter(Boolean)` below, exactly as the previous `?.toString()` (`undefined`) was.
        rawOptions = filteredData.map((d: unknown) => {
          const rawOption: unknown = valueGetter ? valueGetter(d) : get(d, cleanField);
          return rawOption === null || rawOption === undefined ? "" : String(rawOption);
        });
      }
      let optionsList = Array.from(new Set(rawOptions.filter(Boolean)));
      if (optionsList.length === 0) optionsList = [NO_RECORDS_LABEL];

      setOptionsMap((prev) => ({ ...prev, [conditionKey]: optionsList }));
      // eslint-disable-next-line catch-handler/require-promise-reject -- failure surfaced inline via NO_RECORDS option; no upstream propagation
    } catch (error) {
      console.error("Error fetching options:", error);
      setOptionsMap((prev) => ({ ...prev, [conditionKey]: [NO_RECORDS_LABEL] }));
    } finally {
      setLoadingMap((prev) => ({ ...prev, [conditionKey]: false }));
    }
  };

  /* -------------------------
     Add / cancel temporary condition
     ------------------------- */
  const handleAddTemp = () => {
    if (!isAllConditionsValid()) return;
    setTempCondition({
      logic: conditions.length === 0 ? "" : "and",
      field: "",
      operator: "",
      value: "",
      name: "",
    });
  };

  const cancelTempCondition = () => {
    setTempCondition(null);
  };

  /* -------------------------
     Handle changes on temp condition
     - When field/operator/value changes we update tempCondition
     - For value typing, we auto-commit via debounced behavior to conditions
     ------------------------- */
  /* --------------------------------------------
   Helper functions for handleTempChange
   -------------------------------------------- */

  const updateTempConditionField = (
    tempCondition: FilterCondition,
    newValue: string,
    getFilterTypeForField: (field: string) => string,
    setOptionsMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
    setOpenMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  ): FilterCondition | "COMMITTED" => {
    const updated = { ...tempCondition, field: removeLastIndex(newValue), operator: "" };
    const filterType = getFilterTypeForField(updated.field);
    const defaultOpt = DEFAULT_FILTER_OPTIONS[filterType];
    updated.value = defaultOpt?.text ?? "";

    // For MultiSelect, auto-set operator and commit immediately as an existing condition
    if (filterType === "MultiSelect") {
      updated.operator = "Equal";
      updated.value = [];
      const newConditions = [...conditions, updated];
      setConditions(newConditions);
      setTempCondition(null);
      setOptionsMap((prev) => ({ ...prev, temp: [] }));
      setOpenMap((prev) => ({ ...prev, temp: false }));
      return "COMMITTED";
    }

    setOptionsMap((prev) => ({ ...prev, temp: [] }));
    setOpenMap((prev) => ({ ...prev, temp: false }));

    return updated;
  };

  const updateTempConditionOperator = (
    tempCondition: FilterCondition,
    newValue: string,
    getFilterTypeForField: (field: string) => string,
    setOptionsMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
    setOpenMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  ): FilterCondition => {
    const updated = { ...tempCondition, operator: newValue };
    const filterType = getFilterTypeForField(updated.field);
    const operatorKey = normalize(newValue);

    if (filterType === "Date") {
      updated.value = operatorKey === "between" ? { startDate: null, endDate: null } : { date: null };
    }

    setOptionsMap((prev) => ({ ...prev, temp: [] }));
    setOpenMap((prev) => ({ ...prev, temp: false }));

    return updated;
  };

  const checkTempConditionComplete = (
    updated: FilterCondition,
    getFilterTypeForField: (field: string) => string,
    isValidDateValue: (d: dayjs.ConfigType) => boolean,
  ): boolean => {
    if (!updated.field || !updated.operator) {
      return false;
    }

    const filterType = getFilterTypeForField(updated.field);
    const operatorKey = normalize(updated.operator);

    if (TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey)) {
      return true;
    }

    // `isValidDateValue` already rejects a nullish date, so the truthiness checks these branches
    // used to `&&` in only restated it — and made the result a value that had to be asserted back
    // to the declared `boolean`. Callers only ever read it as a condition, so nothing changes.
    if (filterType === "Date") {
      const value = toValueObject(updated.value);
      if (operatorKey === "between") {
        return isValidDateValue(value.startDate) && isValidDateValue(value.endDate);
      }
      return isValidDateValue(value.date);
    }

    if (filterType === "Number") {
      return false;
    }

    if (filterType === "FileSize") {
      const size = toValueObject(updated.value).size;
      return size !== "" && size !== null && size !== undefined && !Number.isNaN(Number(size));
    }

    if (filterType === "MultiSelect") {
      return Array.isArray(updated.value) && updated.value.length > 0;
    }

    return false;
  };

  const handleDebouncedTextCommit = (
    key: string,
    updated: FilterCondition,
    conditions: FilterCondition[],
    setConditions: React.Dispatch<React.SetStateAction<FilterCondition[]>>,
    setTempCondition: React.Dispatch<React.SetStateAction<FilterCondition | null>>,
    setOptionsMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
    setOpenMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    isConditionReady: (cond: FilterCondition) => boolean,
    applyFiltersToGrid: (onlyTheseConditions?: FilterCondition[]) => void,
    debounceTimers: React.MutableRefObject<Record<string, TimeoutId>>,
  ) => {
    if (key !== "value") return;
    const rawValue: FilterConditionValue = updated.value;
    // FileSize stores its typed input on `.size`; unwrap before checking for emptiness.
    const valueForCheck = rawValue && typeof rawValue === "object" && "size" in rawValue ? rawValue.size : rawValue;
    if (valueForCheck === null || valueForCheck === undefined || `${valueForCheck}`.trim() === "") {
      return;
    }

    const tempKey = "temp";
    if (debounceTimers.current[tempKey]) {
      clearTimeout(debounceTimers.current[tempKey]!);
    }

    debounceTimers.current[tempKey] = setTimeout(() => {
      const newConditions = [...conditions, updated];
      setConditions(newConditions);
      setTempCondition(null);
      setOptionsMap((prev) => ({ ...prev, temp: [] }));
      setOpenMap((prev) => ({ ...prev, temp: false }));
      const ready = newConditions.filter(isConditionReady);
      applyFiltersToGrid(ready);
      delete debounceTimers.current[tempKey];
    }, DEBOUNCE_MS);
  };

  const commitTempCondition = (
    updated: FilterCondition,
    conditions: FilterCondition[],
    setConditions: React.Dispatch<React.SetStateAction<FilterCondition[]>>,
    setTempCondition: React.Dispatch<React.SetStateAction<FilterCondition | null>>,
    setOptionsMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
    setOpenMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    isConditionReady: (cond: FilterCondition) => boolean,
    applyFiltersToGrid: (onlyTheseConditions?: FilterCondition[]) => void,
  ) => {
    const newConditions = [...conditions, updated];
    setConditions(newConditions);
    setTempCondition(null);
    setOptionsMap((prev) => ({ ...prev, temp: [] }));
    setOpenMap((prev) => ({ ...prev, temp: false }));
    const ready = newConditions.filter(isConditionReady);
    applyFiltersToGrid(ready);
  };

  /* --------------------------------------------
   Refactored handleTempChange
   -------------------------------------------- */

  const handleTempChange = (key: string, newValue: FilterConditionValue) => {
    if (!tempCondition) return;

    let updated: FilterCondition = { ...tempCondition, [key]: newValue };

    // The "field" / "operator" keys always carry a plain string; the "value" key carries the arm.
    if (key === "field") {
      const result = updateTempConditionField(
        tempCondition,
        toSelectionString(newValue),
        getFilterTypeForField,
        setOptionsMap,
        setOpenMap,
      );
      if (result === "COMMITTED") return;
      updated = result;
    }

    if (key === "operator") {
      updated = updateTempConditionOperator(
        tempCondition,
        toSelectionString(newValue),
        getFilterTypeForField,
        setOptionsMap,
        setOpenMap,
      );
    }

    setTempCondition(updated);

    const filterType = getFilterTypeForField(updated.field);
    const isComplete = checkTempConditionComplete(updated, getFilterTypeForField, isValidDateValue);

    // Number/FileSize require free typing; committing immediately unmounts the temp row and
    // users lose focus after the first digit. Route them through the debounced commit path
    // (same as Text) so the temp row stays alive while typing.
    const isFreeTypingValue = key === "value" && (filterType === "FileSize" || filterType === "Number");

    if (isComplete && !isFreeTypingValue) {
      commitTempCondition(
        updated,
        conditions,
        setConditions,
        setTempCondition,
        setOptionsMap,
        setOpenMap,
        isConditionReady,
        applyFiltersToGrid,
      );
      return;
    }

    if (filterType === "Text" || filterType === "FileSize" || filterType === "Number") {
      handleDebouncedTextCommit(
        key,
        updated,
        conditions,
        setConditions,
        setTempCondition,
        setOptionsMap,
        setOpenMap,
        isConditionReady,
        applyFiltersToGrid,
        debounceTimers,
      );
    }
  };

  /* -------------------------
     Handle change on existing condition rows
     ------------------------- */
  const handleChange = (index: number, key: keyof FilterCondition, value: FilterConditionValue) => {
    const updated: FilterCondition[] = [...conditions];
    // `index` always addresses an existing row (it comes from the render loop), so this guard never
    // fires at runtime; it is what lets the writes below be plain typed property writes rather than
    // an erased `Record<string, unknown>` view of a possibly-missing row.
    const target = updated[index];
    if (!target) return;

    if (key === "field") {
      target.field = removeLastIndex(toSelectionString(value));
      target.operator = "";
      const filterType = getFilterTypeForField(target.field);
      const defaultOpt = DEFAULT_FILTER_OPTIONS[filterType];
      target.value = defaultOpt?.text ?? "";
      if (filterType === "MultiSelect") {
        target.operator = "Equal";
        target.value = [];
      }
      const conditionKey = `condition-${index}`;
      setOptionsMap((prev) => ({ ...prev, [conditionKey]: [] }));
      setOpenMap((prev) => ({ ...prev, [conditionKey]: false }));
    } else if (key === "value") {
      target.value = value;
    } else {
      // "operator" / "logic" / "name" / "id" all hold a plain string.
      target[key] = toSelectionString(value);
    }

    if (key === "operator") {
      const filterType = getFilterTypeForField(target.field);
      const operatorKey = normalize(toSelectionString(value));

      if (filterType === "Date") {
        target.value = operatorKey === "between" ? { startDate: null, endDate: null } : { date: null };
      }

      const conditionKey = `condition-${index}`;
      setOptionsMap((prev) => ({ ...prev, [conditionKey]: [] }));
      setOpenMap((prev) => ({ ...prev, [conditionKey]: false }));
    }

    setConditions(updated);

    if (key === "value") {
      const conditionKey = `condition-${index}`;
      applyFiltersDebounced(conditionKey);
    }
  };

  const handleDelete = (index: number) => {
    const updated = [...conditions];
    updated.splice(index, 1);
    setConditions(updated);
    const ready = updated.filter(isConditionReady);
    applyFiltersToGrid(ready);
  };

  /* -------------------------
     Input handlers for Autocomplete / text typing
     ------------------------- */
  const handleInputChange =
    (conditionKey: string, field: string, operator: string) => (_: React.SyntheticEvent, newInputValue: string) => {
      const shouldOpen = !!newInputValue;
      setOpenMap((prev) => ({ ...prev, [conditionKey]: shouldOpen }));

      if (conditionKey.startsWith("temp")) {
        handleTempChange("value", newInputValue);
      } else {
        const idx = Number.parseInt(conditionKey.replace("condition-", ""), 10);
        handleChange(idx, "value", newInputValue);
      }

      if (shouldOpen) {
        fetchOptions(newInputValue, field, operator, conditionKey);
      }
    };

  const handleOptionSelect =
    (conditionKey: string, _field: string) => (_: React.SyntheticEvent, newValue: string | null) => {
      const value = typeof newValue === "string" ? newValue : "";

      if (conditionKey.startsWith("temp")) {
        // Clear pending debounce for temp if any
        if (debounceTimers.current["temp"]) {
          clearTimeout(debounceTimers.current["temp"]!);
          delete debounceTimers.current["temp"];
        }

        handleTempChange("value", value);

        // If temp has enough data, commit it immediately
        if (tempCondition?.field && tempCondition.operator) {
          const updated: FilterCondition = { ...tempCondition, value };
          const newConditions = [...conditions, updated];
          setConditions(newConditions);
          setTempCondition(null);
          setOptionsMap((prev) => ({ ...prev, temp: [] }));
          setOpenMap((prev) => ({ ...prev, temp: false }));
          const ready = newConditions.filter(isConditionReady);
          applyFiltersToGrid(ready);
        }
      } else {
        // existing condition
        const idx = Number.parseInt(conditionKey.replace("condition-", ""), 10);

        // clear pending debounce for this condition if exists
        if (debounceTimers.current[conditionKey]) {
          clearTimeout(debounceTimers.current[conditionKey]!);
          delete debounceTimers.current[conditionKey];
        }

        const updated: FilterCondition[] = [...conditions];
        updated[idx]!.value = value;
        setConditions(updated);

        const ready = updated.filter(isConditionReady);
        applyFiltersToGrid(ready);
      }

      setOpenMap((prev) => ({ ...prev, [conditionKey]: false }));
    };

  /* -------------------------
     Date handlers
     ------------------------- */
  const applyDateNormalized = (dateValue: Dayjs, dateOperatorKey: string) => {
    // For certain operators we want startOf/endOf day semantics
    if (dateOperatorKey === "lessthan") {
      return dateValue.startOf("day");
    } else if (dateOperatorKey === "lessthanorequal") {
      return dateValue.endOf("day");
    } else if (dateOperatorKey === "greaterthan") {
      return dateValue.endOf("day");
    } else if (dateOperatorKey === "greaterthanorequal") {
      return dateValue.startOf("day");
    }
    return dateValue;
  };

  const handleDateChange = (index: number, newDate: Dayjs | null, isTemp = false) => {
    if (!newDate) return;
    // `conditionAny` predates this typing pass; it is just `conditions` under another name.
    const conditionAny: FilterCondition[] = conditions;
    const dateObj = dayjs(newDate);
    const operator = isTemp ? tempCondition?.operator : conditionAny[index]!.operator;
    const operatorKey = normalize(operator ?? "");
    const value = { date: applyDateNormalized(dateObj, operatorKey) };

    if (isTemp) {
      handleTempChange("value", value);
    } else {
      const updated: FilterCondition[] = [...conditions];
      updated[index]!.value = value;
      setConditions(updated);
      const ready = updated.filter(isConditionReady);
      applyFiltersToGrid(ready);
    }
  };

  const handleBetweenDateChange = (
    index: number,
    dateKey: "startDate" | "endDate",
    newDate: Dayjs | null,
    isTemp = false,
  ) => {
    if (!newDate) return;
    const dateObj = dayjs(newDate);

    if (isTemp) {
      const updatedVal = { ...toValueObject(tempCondition?.value), [dateKey]: dateObj };
      handleTempChange("value", updatedVal);
    } else {
      const updated: FilterCondition[] = [...conditions];
      updated[index]!.value = { ...toValueObject(updated[index]!.value), [dateKey]: dateObj };
      setConditions(updated);

      // If both dates are now set (endDate after setting), try apply
      if (dateKey === "endDate" && toValueObject(updated[index]!.value).startDate) {
        const ready = updated.filter(isConditionReady);
        applyFiltersToGrid(ready);
      }
    }
  };

  /* -------------------------
     Render helpers for different value inputs
     ------------------------- */
  const renderDateInput = (cond: FilterCondition, idx: number, isTemp = false) => {
    const operatorKey = normalize(cond.operator || "");
    const isDisabled = !cond.operator || TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey);

    if (operatorKey === "between") {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex gap-2 flex-1" style={{ minWidth: "18.75rem" }}>
            <DatePicker
              disabled={isDisabled}
              value={toDayjsOrNull(toValueObject(cond.value).startDate)}
              onChange={(d) => handleBetweenDateChange(idx, "startDate", d, isTemp)}
              onAccept={(d) => handleBetweenDateChange(idx, "startDate", d, isTemp)}
              format="MMM DD, YYYY"
              slots={{ popper: ModalDatePickerPopper }}
              slotProps={{
                textField: {
                  size: "small",
                  className: " px-2 py-1 rounded flex-1",
                  placeholder: t("Start date"),
                  sx: { minWidth: "8.75rem" },
                },
              }}
            />
            <DatePicker
              disabled={isDisabled}
              value={toDayjsOrNull(toValueObject(cond.value).endDate)}
              onChange={(d) => handleBetweenDateChange(idx, "endDate", d, isTemp)}
              onAccept={(d) => handleBetweenDateChange(idx, "endDate", d, isTemp)}
              format="MMM DD, YYYY"
              slots={{ popper: ModalDatePickerPopper }}
              slotProps={{
                textField: {
                  size: "small",
                  className: " px-2 py-1 rounded flex-1",
                  placeholder: t("End date"),
                  sx: { minWidth: "8.75rem" },
                },
              }}
            />
          </div>
        </LocalizationProvider>
      );
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          disabled={isDisabled}
          value={toDayjsOrNull(toValueObject(cond.value).date)}
          onChange={(d) => handleDateChange(idx, d, isTemp)}
          onAccept={(d) => handleDateChange(idx, d, isTemp)}
          format="MMM DD, YYYY"
          slots={{ popper: ModalDatePickerPopper }}
          slotProps={{
            textField: {
              size: "small",
              className: " px-2 py-1 rounded flex-1 min-w-0",
              placeholder: t("Select date"),
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const renderTextOrNumberInput = (cond: FilterCondition, idx: number, isTemp = false) => {
    const type = getFilterTypeForField(cond.field);
    const operatorKey = normalize(cond.operator || "");
    const conditionKey = isTemp ? "temp" : `condition-${idx}`;
    const isDisabled = !cond.operator || TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey);
    const shouldFetchOptions =
      operatorKey === "equal" ||
      operatorKey === "startswith" ||
      operatorKey === "contains" ||
      operatorKey === "notequal" ||
      operatorKey === "doesnotstartwith" ||
      operatorKey === "doesnotendwith" ||
      operatorKey === "doesnotcontain" ||
      operatorKey === "endswith";

    const displayValue = () => {
      if (cond.value === null || cond.value === undefined) return "";
      // A date arm is edited through the DatePicker, so it shows as empty text here.
      const valueObj = toValueObject(cond.value);
      if (valueObj.date !== undefined || valueObj.startDate !== undefined) {
        return "";
      }
      return cond.value.toString();
    };

    return (
      <AutoComplete
        freeSolo
        PopperComponent={ModalAutoCompletePopper}
        open={openMap[conditionKey] || false}
        options={optionsMap[conditionKey] || []}
        onClose={() => setOpenMap((prev) => ({ ...prev, [conditionKey]: false }))}
        getOptionDisabled={(option: string) => option === NO_RECORDS_LABEL}
        getOptionLabel={(option: string) => (typeof option === "string" ? option : "")}
        value={displayValue()}
        filterOptions={(x: string[]) => x}
        onInputChange={handleInputChange(conditionKey, cond.field, cond.operator)}
        onChange={handleOptionSelect(conditionKey, cond.field)}
        disabled={isDisabled}
        loading={loadingMap[conditionKey]}
        onOpen={() => {
          if (shouldFetchOptions && cond.operator && cond.field) {
            setOpenMap((prev) => ({ ...prev, [conditionKey]: true }));
            const value = displayValue();
            // Fetch options even if value is empty to show auto-suggestions
            fetchOptions(value || "", cond.field, cond.operator, conditionKey);
          }
        }}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
          <li
            {...props}
            title={typeof option === "string" ? option : ""}
            style={{ maxWidth: "12.5rem", width: "12.5rem" }}
          >
            {option ? <Text sx={{ maxWidth: "100%" }}>{option}</Text> : <Text className="italic">{t("empty")}</Text>}
          </li>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldComponent
            {...params}
            size="small"
            placeholder={t("Enter value")}
            className=" px-2 py-1 rounded flex-1 min-w-0"
            type={type === "Number" ? "number" : "text"}
            disabled={isDisabled}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingMap[conditionKey] ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        sx={{ flex: 1, minWidth: "6.25rem" }}
      />
    );
  };

  const renderFallbackInput = (cond: FilterCondition, idx: number, isTemp = false) => {
    const operatorKey = normalize(cond.operator || "");
    const isDisabled = !cond.operator || TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey);

    return (
      <TextFieldComponent
        size="small"
        placeholder={t("Enter value")}
        className="border px-2 py-1 rounded flex-1 min-w-0"
        value={cond.value || ""}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          isTemp ? handleTempChange("value", e.target.value) : handleChange(idx, "value", e.target.value)
        }
        disabled={isDisabled}
      />
    );
  };

  const renderFileSizeInput = (cond: FilterCondition, idx: number, isTemp = false) => {
    const operatorKey = normalize(cond.operator || "");
    const isDisabled = !cond.operator || TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey);

    // CUBE-17621: normalise the FileSize value into `{size, unit}` shape so the
    // size input always receives a primitive (string|number) rather than the
    // whole condition value object. The previous fallback chain
    // `cond.value?.size ?? cond.value ?? ""` returned the object itself when
    // `cond.value` happened to be `{size: undefined, unit: "MB"}` (e.g. after a
    // saved-view round-trip that flattened the typed state) — the `<input
    // type="number">` then saw an object as its `value`, coerced it to
    // "[object Object]", rejected the value, and silently swallowed every
    // subsequent keystroke. Reading the size primitive directly fixes the
    // "cannot enter values" symptom.
    // Deliberately an `unknown` view: the checks below have to survive a value whose runtime shape
    // does not match `FilterValueObject`, which is exactly what CUBE-17621 was.
    const valueObj: { size?: unknown; unit?: unknown } =
      typeof cond.value === "object" && cond.value !== null && !Array.isArray(cond.value) ? cond.value : {};
    const sizeRaw =
      valueObj.size !== undefined && valueObj.size !== null
        ? valueObj.size
        : typeof cond.value === "string" || typeof cond.value === "number"
          ? cond.value
          : "";
    const sizeValue = typeof sizeRaw === "string" || typeof sizeRaw === "number" ? sizeRaw : "";
    const unitValue = typeof valueObj.unit === "string" && valueObj.unit ? valueObj.unit : "MB";

    return (
      <div className="flex gap-2 flex-1">
        <TextFieldComponent
          size="small"
          placeholder={t("Enter size (e.g., 100)")}
          className="px-2 py-1 rounded flex-1"
          sx={{ minWidth: "5rem" }}
          type="number"
          value={sizeValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = { size: e.target.value, unit: unitValue };
            isTemp ? handleTempChange("value", newValue) : handleChange(idx, "value", newValue);
          }}
          disabled={isDisabled}
        />
        <AutoComplete
          disableClearable
          PopperComponent={ModalAutoCompletePopper}
          options={["B", "KB", "MB", "GB"]}
          value={unitValue}
          minWidth={0}
          onChange={(e: React.SyntheticEvent, val: string) => {
            const newValue = { size: sizeValue, unit: val };
            isTemp ? handleTempChange("value", newValue) : handleChange(idx, "value", newValue);
          }}
          getOptionLabel={(params: string) => params || ""}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent {...params} size="small" className="px-2 py-1 rounded" />
          )}
          sx={{ width: "5.625rem", flexShrink: 0 }}
          disabled={isDisabled}
        />
      </div>
    );
  };

  const renderMultiSelectInput = (cond: FilterCondition, idx: number, isTemp = false) => {
    const conditionKey = isTemp ? "temp" : `condition-${idx}`;
    const operatorKey = normalize(cond.operator || "");
    const isDisabled = !cond.operator || TEXT_FIELD_DISABLED_OPTIONS.has(operatorKey);
    const selectedValues: string[] = Array.isArray(cond.value) ? cond.value : [];

    return (
      <AutoComplete
        multiple
        PopperComponent={ModalAutoCompletePopper}
        open={openMap[conditionKey] || false}
        options={(optionsMap[conditionKey] || []).filter((opt: string) => opt !== NO_RECORDS_LABEL)}
        onClose={() => setOpenMap((prev) => ({ ...prev, [conditionKey]: false }))}
        getOptionLabel={(option: string) => (typeof option === "string" ? option : "")}
        value={selectedValues}
        disableCloseOnSelect
        onChange={(_: React.SyntheticEvent, newValue: string[]) => {
          if (isTemp) {
            handleTempChange("value", newValue);
          } else {
            handleChange(idx, "value", newValue);
          }
        }}
        onInputChange={(_: React.SyntheticEvent, newInputValue: string) => {
          const shouldOpen = true;
          setOpenMap((prev) => ({ ...prev, [conditionKey]: shouldOpen }));
          fetchOptions(newInputValue, cond.field, cond.operator, conditionKey);
        }}
        disabled={isDisabled}
        loading={loadingMap[conditionKey]}
        onOpen={() => {
          if (cond.operator && cond.field) {
            setOpenMap((prev) => ({ ...prev, [conditionKey]: true }));
            fetchOptions("", cond.field, cond.operator, conditionKey);
          }
        }}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
          <li
            {...props}
            title={typeof option === "string" ? option : ""}
            style={{ minWidth: "12.5rem", maxWidth: "12.5rem", width: "12.5rem" }}
          >
            <Text>{option}</Text>
          </li>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldComponent
            {...params}
            size="small"
            placeholder={selectedValues.length === 0 ? t("Enter value") : ""}
            className=" px-2 py-1 rounded flex-1 min-w-0"
            disabled={isDisabled}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingMap[conditionKey] ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        sx={{ flex: 1, minWidth: "6.25rem" }}
      />
    );
  };

  const renderValueInput = (cond: FilterCondition, idx: number, isTemp = false) => {
    const type = getFilterTypeForField(cond.field);
    if (type === "Date") {
      return renderDateInput(cond, idx, isTemp);
    }
    if (type === "FileSize") {
      return renderFileSizeInput(cond, idx, isTemp);
    }
    if (type === "MultiSelect") {
      return renderMultiSelectInput(cond, idx, isTemp);
    }
    if (type === "Text" || type === "Number") {
      return renderTextOrNumberInput(cond, idx, isTemp);
    }
    return renderFallbackInput(cond, idx, isTemp);
  };

  /* -------------------------
     Render rows (existing conditions)
     ------------------------- */
  const renderRows = () =>
    conditions.map((cond: FilterCondition, idx: number) => (
      <div key={`${cond.field}_${idx}`} className="flex items-center gap-2 px-2 py-2 mb-2 flex-wrap">
        {idx === 0 ? (
          <Text className="w-16 text-sm font-normal">{t("Where")}</Text>
        ) : (
          <AutoComplete
            disableClearable
            PopperComponent={ModalAutoCompletePopper}
            options={["and", "or"]}
            getOptionLabel={(params: string) => (params === "and" ? t("and") : t("or"))}
            value={cond.logic || "and"}
            onChange={(e: React.SyntheticEvent, val: string) => handleChange(idx, "logic", val)}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldComponent
                {...params}
                size="small"
                placeholder={t("Select")}
                className=" py-1 rounded shrink-0"
              />
            )}
            sx={{ width: "4.375rem", "& .MuiOutlinedInput-root": { height: "2rem" } }}
          />
        )}

        <AutoComplete
          disableClearable
          PopperComponent={FieldPickerPopper}
          options={columnDefinitions.filter((col: ColumnDefinition) => {
            const colId = col.id || col.field;
            if (["_checkbox_selector", "_move"].includes(colId)) return false;
            return !col.disableFiltering;
          })}
          value={
            columnDefinitions.find((col: ColumnDefinition) => col.field === removeLastIndex(cond.field)) ?? undefined
          }
          getOptionLabel={(params: ColumnDefinition) => params.name || ""}
          onChange={(e: React.SyntheticEvent, val: ColumnDefinition) => handleChange(idx, "field", val.field)}
          slotProps={{
            popper: { style: { zIndex: 1501 } },
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ColumnDefinition) => (
            <li {...props} title={option.name || ""} style={{ minWidth: "12.5rem", maxWidth: "28rem" }}>
              <Text sx={{ whiteSpace: "normal", wordBreak: "break-word", width: "100%" }}>{option.name || ""}</Text>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent
              {...params}
              size="small"
              placeholder={t("Select field")}
              className=" px-2 py-1 rounded flex-1 min-w-0"
              title={
                columnDefinitions.find((col: ColumnDefinition) => col.field === removeLastIndex(cond.field))?.name || ""
              }
            />
          )}
          sx={{ flex: 1, minWidth: "6.25rem", maxWidth: "12.5rem" }}
        />

        <AutoComplete
          disableClearable
          PopperComponent={FieldPickerPopper}
          options={cond.field ? FILTER_OPTIONS[getFilterTypeForField(cond.field)] || TEXT_FILTER_OPTIONS : []}
          value={cond.operator || undefined}
          onChange={(e: React.SyntheticEvent, val: string) => handleChange(idx, "operator", val)}
          getOptionLabel={(params: string) => params || ""}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent
              {...params}
              size="small"
              placeholder={t("Select operator")}
              className=" px-2 py-1 rounded flex-1 min-w-0"
            />
          )}
          sx={{ flex: 1, minWidth: "6.25rem" }}
          disabled={!cond.field}
        />

        {renderValueInput(cond, idx, false)}

        <div className="shrink-0">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} iconButton={true} onClick={() => handleDelete(idx)}>
            <IconComponent name="delete" width="1.25rem" height="1.25rem" />
          </ButtonComponent>
        </div>
      </div>
    ));

  /* -------------------------
     Render temporary input (when adding a new condition)
     ------------------------- */
  const renderTempRow = () => {
    if (!tempCondition) return null;

    return (
      <div className="flex items-center gap-2 px-2 py-2 mb-2 flex-wrap">
        {conditions.length === 0 ? (
          <Text className="w-16 text-sm font-normal">{t("Where")}</Text>
        ) : (
          <AutoComplete
            disableClearable
            PopperComponent={ModalAutoCompletePopper}
            options={["and", "or"]}
            minWidth={0}
            getOptionLabel={(params: string) => (params === "and" ? t("and") : t("or"))}
            value={tempCondition.logic || "and"}
            onChange={(e: React.SyntheticEvent, val: string) => handleTempChange("logic", val)}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldComponent
                {...params}
                size="small"
                placeholder={t("Select")}
                className=" py-1 rounded flex-shrink-0"
              />
            )}
            sx={{ width: "4.375rem", "& .MuiOutlinedInput-root": { height: "2rem" } }}
          />
        )}

        <AutoComplete
          disableClearable
          PopperComponent={FieldPickerPopper}
          options={columnDefinitions.filter((col: ColumnDefinition) => {
            const colId = col.id || col.field;
            if (["_checkbox_selector", "_move"].includes(colId)) return false;
            return !col.disableFiltering;
          })}
          value={columnDefinitions.find((col: ColumnDefinition) => col.field === tempCondition.field) ?? undefined}
          onChange={(e: React.SyntheticEvent, val: ColumnDefinition) => handleTempChange("field", val.field)}
          getOptionLabel={(params: ColumnDefinition) => params.name || ""}
          slotProps={{
            popper: { style: { zIndex: 1501 } },
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ColumnDefinition) => (
            <li {...props} title={option.name || ""} style={{ minWidth: "12.5rem", maxWidth: "28rem" }}>
              <Text sx={{ whiteSpace: "normal", wordBreak: "break-word", width: "100%" }}>{option.name || ""}</Text>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent
              {...params}
              size="small"
              placeholder={t("Select field")}
              className="px-2 py-1 rounded flex-1 min-w-0"
              title={columnDefinitions.find((col: ColumnDefinition) => col.field === tempCondition.field)?.name || ""}
            />
          )}
          sx={{ flex: 1, minWidth: "6.25rem", maxWidth: "12.5rem" }}
        />

        <AutoComplete
          disableClearable
          PopperComponent={FieldPickerPopper}
          options={
            tempCondition.field ? FILTER_OPTIONS[getFilterTypeForField(tempCondition.field)] || TEXT_FILTER_OPTIONS : []
          }
          value={tempCondition.operator || undefined}
          getOptionLabel={(params: string) => params || ""}
          onChange={(e: React.SyntheticEvent, val: string) => handleTempChange("operator", val)}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent
              {...params}
              size="small"
              placeholder={t("Select operator")}
              className=" px-2 py-1 rounded flex-1 min-w-0"
            />
          )}
          sx={{ flex: 1, minWidth: "6.25rem" }}
          disabled={!tempCondition.field}
        />

        {renderValueInput(tempCondition, -1, true)}

        <div className="shrink-0">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} iconButton={true} onClick={cancelTempCondition}>
            <IconComponent name="delete" width="1.25rem" height="1.25rem" />
          </ButtonComponent>
        </div>
      </div>
    );
  };

  /* -------------------------
     Main render
     ------------------------- */
  if (!isOpen) return null;

  return (
    <Popper open={isOpen} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1500 }} sx={{ mt: "2.5rem" }}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            width,
            maxHeight: "25rem",
            borderRadius: "0.5rem",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            padding: "1.5rem !important",
          }}
        >
          <div className="flex justify-between items-center border-b border-[#C6D8EC]  pb-4 shrink-0">
            <Text className="text-2xl font-semibold">{t("Filter")}</Text>

            <ButtonComponent
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={handleAddTemp}
              disabled={!isAllConditionsValid() || !!tempCondition}
              startIcon={<AddIcon />}
            >
              <Text>{t("Add")}</Text>
            </ButtonComponent>
          </div>

          <div className="flex-1 overflow-y-auto  pt-4">
            {conditions.length > 0 || tempCondition ? (
              renderRows()
            ) : (
              <Text className="text-[#666A84]">{t("No filter conditions are applied.")}</Text>
            )}

            {renderTempRow()}

            <div ref={bottomRef} className="w-0 h-0" />
          </div>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default FilterModal;
